import { configureStore, combineReducers } from '@reduxjs/toolkit';
import type { StateFromReducersMapObject, ActionFromReducersMapObject } from '@reduxjs/toolkit';
import { actionConverterMiddleware } from 'app/middleware/actionConverterMiddleware.js';
import { paramResolverMiddleware } from 'app/middleware/paramResolverMiddleware.js';
import { apiMiddleware } from 'app/middleware/apiMiddleware.js';
import { snowflakeQueryMiddleware } from 'app/middleware/snowflakeQueryMiddleware.js';
import { graphBuilderMiddleware } from 'app/middleware/graphBuilderMiddleware.js';
import { sideEffectMiddleware } from 'app/middleware/sideEffectMiddleware.js';
import { webSocketMiddleware } from 'app/middleware/webSocketMiddleware.js';
import { persistMiddleware } from 'app/middleware/persistMiddleware.js';
import { refreshJwtToken } from 'app/store/auth';
import { initializeHashParser } from 'app/routes/hashParser.js';
import { migrateExploreData } from 'app/store/exploreData/migrate.js';
import { actionSanitizer, actionsDenylist, stateSanitizer } from './sanitizer.js';
import { loadPersistedState } from './persistState';
import { reducers } from './reducers';
import type { Reducers } from './reducers';

declare const MODE: string;

const reducer = combineReducers(reducers);

const DEVTOOLS_OPTIONS = {
    actionSanitizer,
    stateSanitizer,
    actionsDenylist,
    maxAge: 200,
    trace: true
};

const middleware = [
    actionConverterMiddleware,
    paramResolverMiddleware,
    webSocketMiddleware(refreshJwtToken),
    sideEffectMiddleware,
    apiMiddleware,
    snowflakeQueryMiddleware,
    graphBuilderMiddleware
];

export const store = configureStore({
    reducer,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            // https://redux-toolkit.js.org/usage/usage-guide#working-with-non-serializable-data
            serializableCheck: {
                ignoreActions: true,
                ignoreState: true
            }
        })
            .prepend(persistMiddleware.middleware)
            .concat([...middleware]),
    devTools: MODE === 'development' ? DEVTOOLS_OPTIONS : false
});

export type Store = {
    dispatch: (action: ActionFromReducersMapObject<Reducers>) => void;
    getState: () => StateFromReducersMapObject<Reducers>;
};
export type AppDispatch = Store['dispatch'];

loadPersistedState(store);
initializeHashParser(store);
migrateExploreData(store);
